/* eslint-disable @next/next/no-img-element */
import React, { useState, useRef, useEffect, forwardRef } from "react";
import type { KeyboardEvent } from "react";
import Modal from "../Modal/Modal";
import clsx from "clsx";
import { useGesture } from "@use-gesture/react";

export type SmartImageProps = React.ImgHTMLAttributes<HTMLImageElement> & {
  src: string;
  alt: string;
  width?: number | string;
  height?: number | string;
  placeholderSrc?: string;
  style?: React.CSSProperties;
  className?: string;
  frameClassName?: string;
  showZoomButton?: boolean;
  disableZoom?: boolean;
  tooltip?: React.ReactNode;
  onLoadComplete?: () => void;
  onError?: () => void;
  customZoomContent?: React.ReactNode;
  theme?: "light" | "dark";
  responsiveSrcs?: {
    srcSet: string;
    sizes?: string;
  };
  sources?: Array<{
    srcSet: string;
    type: string;
    sizes?: string;
  }>;
  priority?: boolean;
  placeholder?: "blur" | "empty";
  blurDataURL?: string;
  layout?: "fill" | "responsive" | "fixed";
  quality?: number;
  onLoadingProgress?: (progress: number) => void;
  errorFallback?: React.ReactNode;
  asBackground?: boolean;
  fetchPriority?: "high" | "low" | "auto";
  ariaHidden?: boolean;
  zoomable?: boolean;
};

const SmartImage = forwardRef<HTMLImageElement, SmartImageProps>(
  (props, ref) => {
    const {
      src,
      alt,
      width,
      height,
      placeholderSrc,
      style,
      className,
      frameClassName,
      showZoomButton,
      disableZoom,
      tooltip,
      onLoadComplete,
      onError,
      customZoomContent,
      theme = "light",
      responsiveSrcs,
      sources,
      priority = false,
      placeholder = "empty",
      blurDataURL,
      layout = "responsive",
      quality,
      onLoadingProgress,
      errorFallback,
      asBackground,
      fetchPriority,
      ariaHidden,
      zoomable = false,
      ...rest
    } = props;

    const [loaded, setLoaded] = useState(false);
    const [zoomed, setZoomed] = useState(false);
    const [error, setError] = useState(false);
    const [scale, setScale] = useState(1);
    const [internalSrc, setInternalSrc] = useState<string | undefined>(
      undefined
    );
    const zoomRef = useRef<HTMLDivElement>(null);

    useGesture(
      {
        onPinch: ({ offset: [d] }) => {
          const newScale = Math.max(1, Math.min(1 + d / 200, 4));
          setScale(newScale);
        },
        onPinchEnd: () => {},
      },
      {
        target: zoomRef,
        eventOptions: { passive: false },
        pinch: { scaleBounds: { min: 1, max: 4 }, rubberband: true },
        enabled: zoomed,
      }
    );

    useEffect(() => {
      if (!zoomed) return;
      const handleKey = (e: KeyboardEvent | KeyboardEventInit) => {
        if (e.key === "Escape") setZoomed(false);
      };
      window.addEventListener("keydown", handleKey as EventListener);
      return () =>
        window.removeEventListener("keydown", handleKey as EventListener);
    }, [zoomed]);

    useEffect(() => {
      if (zoomed && zoomRef.current) {
        zoomRef.current.focus();
      }
    }, [zoomed]);

    useEffect(() => {
      if (!zoomed) return;
      const focusableSelectors =
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
      const modal = zoomRef.current;
      if (!modal) return;

      const focusableEls =
        modal.querySelectorAll<HTMLElement>(focusableSelectors);
      const firstEl = focusableEls[0];
      const lastEl = focusableEls[focusableEls.length - 1];

      function trapFocus(e: Event) {
        const event = e as unknown as KeyboardEvent;
        if (event.key !== "Tab") return;
        if (focusableEls.length === 0) return;

        if (event.shiftKey) {
          if (document.activeElement === firstEl) {
            event.preventDefault();
            lastEl.focus();
          }
        } else {
          if (document.activeElement === lastEl) {
            event.preventDefault();
            firstEl.focus();
          }
        }
      }

      modal.addEventListener("keydown", trapFocus);
      return () => modal.removeEventListener("keydown", trapFocus);
    }, [zoomed]);

    useEffect(() => {
      if (!zoomed) setScale(1);
    }, [zoomed]);

    useEffect(() => {
      if (!onLoadingProgress) {
        setInternalSrc(src);
        return;
      }
      const xhr: XMLHttpRequest = new XMLHttpRequest();
      xhr.open("GET", src, true);
      xhr.responseType = "blob";
      xhr.onprogress = (event) => {
        if (event.lengthComputable && onLoadingProgress) {
          const percent = Math.round((event.loaded / event.total) * 100);
          onLoadingProgress(percent);
        }
      };
      xhr.onload = () => {
        if (xhr && xhr.status === 200) {
          const url = URL.createObjectURL(xhr.response);
          setInternalSrc(url);
        } else {
          setInternalSrc(src);
        }
      };
      xhr.onerror = () => setInternalSrc(src);
      xhr.send();

      return () => {
        xhr.abort();
      };
    }, [src, onLoadingProgress]);

    useEffect(() => {
      return () => {
        if (internalSrc?.startsWith("blob:")) URL.revokeObjectURL(internalSrc);
      };
    }, [internalSrc]);

    let containerStyle: React.CSSProperties = { ...style };

    if (layout === "fill") {
      containerStyle = {
        ...containerStyle,
        position: "relative",
        width: "100%",
        height: "100%",
      };
    } else if (layout === "responsive") {
      if (width && height) {
        const ratio = (Number(height) / Number(width)) * 100;
        containerStyle = {
          ...containerStyle,
          position: "relative",
          width: "100%",
          paddingBottom: `${ratio}%`,
          // minHeight: 1, // opcional, mas NÃO height: 0!
          // height: 0,    // REMOVIDO!
        };
      }
    } else if (layout === "fixed") {
      containerStyle = {
        ...containerStyle,
        width: typeof width !== "undefined" ? width : "auto",
        height: typeof height !== "undefined" ? height : "auto",
        position: "relative",
      };
    }

    // NOVO: calcula estilos do container para layout responsive
    let responsiveContainerStyle: React.CSSProperties = { ...containerStyle };
    if (layout === "responsive") {
      const w = Number(width) || 1;
      const h = Number(height) || 1;
      responsiveContainerStyle = {
        ...responsiveContainerStyle,
        width: "100%",
        position: "relative",
        aspectRatio: `${w} / ${h}`,
        paddingBottom: undefined,
      };
      // Fallback para browsers antigos:
      if (!("aspectRatio" in document.body.style)) {
        responsiveContainerStyle.aspectRatio = undefined;
        responsiveContainerStyle.paddingBottom = `${(h / w) * 100}%`;
      }
    }

    const getQualityUrl = React.useCallback(
      (url: string) => {
        if (!quality) return url;
        return url.includes("?")
          ? `${url}&quality=${quality}`
          : `${url}?quality=${quality}`;
      },
      [quality]
    );

    const getQualitySrcSet = React.useCallback(
      (srcSet: string) => {
        if (!quality) return srcSet;
        return srcSet
          .split(",")
          .map((item) => {
            const [url, descriptor] = item.trim().split(" ");
            return `${getQualityUrl(url)}${descriptor ? " " + descriptor : ""}`;
          })
          .join(", ");
      },
      [quality, getQualityUrl]
    );

    useEffect(() => {
      if (
        process.env.NODE_ENV !== "production" &&
        (!alt || alt.trim() === "")
      ) {
        // eslint-disable-next-line no-console
        console.warn(
          "SmartImage: O atributo alt não deve ser vazio, exceto para imagens puramente decorativas."
        );
      }
    }, [alt]);

    // Adicione este useEffect para lidar com preload:
    useEffect(() => {
      if (!priority) return;
      const link = document.createElement("link");
      link.rel = "preload";
      link.as = "image";
      link.href = getQualityUrl(src);
      if (responsiveSrcs?.sizes)
        link.setAttribute("imagesizes", responsiveSrcs.sizes);
      document.head.appendChild(link);
      return () => {
        document.head.removeChild(link);
      };
    }, [priority, src, responsiveSrcs, quality, getQualityUrl]);

    // Troque o bloco principal do return por:
    if (asBackground) {
      return (
        <div
          style={{
            ...containerStyle,
            backgroundImage: `url('${getQualityUrl(internalSrc || src)}')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            width: width || "100%",
            height: height || "100%",
          }}
          className={clsx(frameClassName, className)}
          role="img"
          aria-label={alt}
          tabIndex={0}
          aria-hidden={ariaHidden}
          {...rest}
        >
          {error &&
            (errorFallback ? (
              <div className="flex items-center justify-center w-full h-full absolute inset-0">
                {errorFallback}
              </div>
            ) : (
              <div className="flex items-center justify-center w-full h-full bg-gray-100 text-gray-500 absolute inset-0">
                <span>Erro ao carregar imagem</span>
              </div>
            ))}
        </div>
      );
    }

    // NOVO bloco para layout responsive
    if (layout === "responsive" && width && height) {
      return (
        <div
          style={responsiveContainerStyle}
          className={clsx(frameClassName, className)}
        >
          <picture
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none", // evita clique no picture
            }}
          >
            {sources?.map((source, idx) => (
              <source
                key={idx}
                srcSet={getQualitySrcSet(source.srcSet)}
                type={source.type}
                sizes={source.sizes ?? responsiveSrcs?.sizes}
              />
            ))}
            {/* Placeholder image */}
            {placeholder === "blur" && blurDataURL && (
              <img
                src={blurDataURL}
                alt=""
                aria-hidden="true"
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  filter: "blur(16px)",
                  transition: "opacity 0.5s",
                  opacity: loaded ? 0 : 1,
                  zIndex: 0,
                  pointerEvents: "none",
                }}
                tabIndex={-1}
                draggable={false}
              />
            )}
            {placeholder === "empty" && placeholderSrc && (
              <img
                src={placeholderSrc}
                alt=""
                aria-hidden="true"
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  transition: "opacity 0.5s",
                  opacity: loaded ? 0 : 1,
                  zIndex: 0,
                  pointerEvents: "none",
                }}
                tabIndex={-1}
                draggable={false}
              />
            )}
            <img
              src={getQualityUrl(internalSrc || src)}
              alt={alt}
              ref={ref}
              srcSet={
                responsiveSrcs?.srcSet
                  ? getQualitySrcSet(responsiveSrcs.srcSet)
                  : undefined
              }
              sizes={responsiveSrcs?.sizes}
              fetchPriority={fetchPriority}
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "contain",
                transition: "opacity 0.5s",
                opacity: loaded ? 1 : 0,
                cursor:
                  zoomable && !disableZoom
                    ? zoomed
                      ? "zoom-out"
                      : "zoom-in"
                    : undefined,
                pointerEvents: "auto",
                zIndex: 1,
              }}
              onLoad={() => {
                setLoaded(true);
                if (onLoadComplete) onLoadComplete();
              }}
              onError={() => {
                setError(true);
                if (onError) onError();
              }}
              tabIndex={0}
              aria-hidden={ariaHidden}
              {...rest}
            />
            {/* PATCH: Render error fallback inside <picture> */}
            {error &&
              (errorFallback ? (
                <div
                  className="flex items-center justify-center w-full h-full absolute inset-0"
                  style={{
                    zIndex: 2,
                    background: "rgba(255,255,255,0.85)",
                    pointerEvents: "auto",
                  }}
                >
                  {errorFallback}
                </div>
              ) : (
                <div
                  className="flex items-center justify-center w-full h-full bg-gray-100 text-gray-500 absolute inset-0"
                  style={{
                    zIndex: 2,
                    background: "rgba(255,255,255,0.85)",
                    pointerEvents: "auto",
                  }}
                >
                  <span>Erro ao carregar imagem</span>
                </div>
              ))}
          </picture>
          {/* Adicione o botão de zoom e tooltip aqui */}
          {showZoomButton && zoomable && !disableZoom && (
            <div
              className="absolute bottom-2 right-2 p-2 bg-white rounded-full shadow-md cursor-pointer transition-transform transform"
              onClick={(e) => {
                e.stopPropagation();
                setZoomed((prev) => !prev);
              }}
              title={zoomed ? "Reduzir imagem" : "Ampliar imagem"}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 text-gray-800"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                {zoomed ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13.875 18.825A6.978 6.978 0 0012 19.5a6.978 6.978 0 00-1.875-.675M6.343 17.657A9.978 9.978 0 0112 15c2.21 0 4.215.743 5.657 1.843M15.75 3.375A9.978 9.978 0 0112 6c-2.21 0-4.215-.743-5.657-1.843M8.25 20.625A9.978 9.978 0 0112 18c2.21 0 4.215.743 5.657 1.843"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6v6l4.5 4.5M4.5 4.5l15 15"
                  />
                )}
              </svg>
            </div>
          )}
          {tooltip && (
            <div
              className={clsx(
                "absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 px-3 py-1 rounded-md text-sm",
                theme === "light"
                  ? "bg-gray-800 text-white"
                  : "bg-gray-200 text-gray-800"
              )}
            >
              {tooltip}
            </div>
          )}
          {zoomed && (
            <Modal open={zoomed} onClose={() => setZoomed(false)}>
              <div
                ref={zoomRef}
                tabIndex={-1}
                className="flex items-center justify-center w-full h-full p-4"
                style={{ outline: "none" }}
              >
                <div
                  className="relative"
                  style={{
                    maxWidth: "90vw",
                    maxHeight: "90vh",
                    overflow: "hidden",
                    borderRadius: "8px",
                  }}
                >
                  <div
                    className="absolute inset-0 bg-black opacity-50"
                    onClick={() => setZoomed(false)}
                    style={{ cursor: "zoom-out" }}
                  />
                  {customZoomContent ? (
                    customZoomContent
                  ) : (
                    <img
                      src={getQualityUrl(internalSrc || src)}
                      alt={alt}
                      ref={ref}
                      srcSet={
                        responsiveSrcs?.srcSet
                          ? getQualitySrcSet(responsiveSrcs.srcSet)
                          : undefined
                      }
                      sizes={responsiveSrcs?.sizes}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                        cursor: "zoom-out",
                        transition: "transform 0.5s",
                        transform: `scale(${scale})`,
                      }}
                      onLoad={() => setLoaded(true)}
                      onError={() => setError(true)}
                      tabIndex={0}
                      aria-hidden={ariaHidden}
                      {...rest}
                    />
                  )}
                  <div className="absolute top-0 right-0 p-2">
                    <button
                      onClick={() => setZoomed(false)}
                      className="p-2 rounded-full bg-white shadow-md"
                      title="Fechar"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6 text-gray-800"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </Modal>
          )}
        </div>
      );
    }

    return (
      <div style={containerStyle} className={clsx(frameClassName, className)}>
        <img
          src={getQualityUrl(internalSrc || src)}
          alt={alt}
          ref={ref}
          srcSet={
            responsiveSrcs?.srcSet
              ? getQualitySrcSet(responsiveSrcs.srcSet)
              : undefined
          }
          sizes={responsiveSrcs?.sizes}
          fetchPriority={fetchPriority}
          style={{
            ...style,
            width: "100%",
            height: "100%",
            objectFit: "contain",
            transition: "opacity 0.5s",
            opacity: loaded ? 1 : 0,
            cursor:
              zoomable && !disableZoom
                ? zoomed
                  ? "zoom-out"
                  : "zoom-in"
                : undefined,
          }}
          onLoad={() => {
            setLoaded(true);
            if (onLoadComplete) onLoadComplete();
          }}
          onError={() => {
            setError(true);
            if (onError) onError();
          }}
          tabIndex={0}
          aria-hidden={ariaHidden}
          {...rest}
        />
        {error &&
          (errorFallback ? (
            <div className="flex items-center justify-center w-full h-full absolute inset-0">
              {errorFallback}
            </div>
          ) : (
            <div className="flex items-center justify-center w-full h-full bg-gray-100 text-gray-500 absolute inset-0">
              <span>Erro ao carregar imagem</span>
            </div>
          ))}
        {showZoomButton && zoomable && !disableZoom && (
          <div
            className="absolute bottom-2 right-2 p-2 bg-white rounded-full shadow-md cursor-pointer transition-transform transform"
            onClick={(e) => {
              e.stopPropagation();
              setZoomed((prev) => !prev);
            }}
            title={zoomed ? "Reduzir imagem" : "Ampliar imagem"}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-gray-800"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              {zoomed ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13.875 18.825A6.978 6.978 0 0012 19.5a6.978 6.978 0 00-1.875-.675M6.343 17.657A9.978 9.978 0 0112 15c2.21 0 4.215.743 5.657 1.843M15.75 3.375A9.978 9.978 0 0112 6c-2.21 0-4.215-.743-5.657-1.843M8.25 20.625A9.978 9.978 0 0112 18c2.21 0 4.215.743 5.657 1.843"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6v6l4.5 4.5M4.5 4.5l15 15"
                />
              )}
            </svg>
          </div>
        )}
        {tooltip && (
          <div
            className={clsx(
              "absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 px-3 py-1 rounded-md text-sm",
              theme === "light"
                ? "bg-gray-800 text-white"
                : "bg-gray-200 text-gray-800"
            )}
          >
            {tooltip}
          </div>
        )}
        {zoomed && (
          <Modal open={zoomed} onClose={() => setZoomed(false)}>
            <div
              ref={zoomRef}
              tabIndex={-1}
              className="flex items-center justify-center w-full h-full p-4"
              style={{ outline: "none" }}
            >
              <div
                className="relative"
                style={{
                  maxWidth: "90vw",
                  maxHeight: "90vh",
                  overflow: "hidden",
                  borderRadius: "8px",
                }}
              >
                <div
                  className="absolute inset-0 bg-black opacity-50"
                  onClick={() => setZoomed(false)}
                  style={{ cursor: "zoom-out" }}
                />
                {customZoomContent ? (
                  customZoomContent
                ) : (
                  <img
                    src={getQualityUrl(internalSrc || src)}
                    alt={alt}
                    ref={ref}
                    srcSet={
                      responsiveSrcs?.srcSet
                        ? getQualitySrcSet(responsiveSrcs.srcSet)
                        : undefined
                    }
                    sizes={responsiveSrcs?.sizes}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      cursor: "zoom-out",
                      transition: "transform 0.5s",
                      transform: `scale(${scale})`,
                    }}
                    onLoad={() => setLoaded(true)}
                    onError={() => setError(true)}
                    tabIndex={0}
                    aria-hidden={ariaHidden}
                    {...rest}
                  />
                )}
                <div className="absolute top-0 right-0 p-2">
                  <button
                    onClick={() => setZoomed(false)}
                    className="p-2 rounded-full bg-white shadow-md"
                    title="Fechar"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6 text-gray-800"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </Modal>
        )}
      </div>
    );
  }
);

SmartImage.displayName = "SmartImage";

export default SmartImage;
